import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import paymentService from '../../services/paymentService';
import paymentAccountService from '../../services/paymentAccountService';
import { FaSave, FaTimes, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useToast } from '../Shared/Toast';

const PaymentForm = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();

  const [userEnteredAmount, setUserEnteredAmount] = useState('');
  const [proof, setProof] = useState(null);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentAccounts = async () => {
      try {
        const accounts = await paymentAccountService.getActivePaymentAccounts();
        setPaymentAccounts(accounts || []);
      } catch (err) {
        console.error('Failed to fetch payment accounts:', err);
        error('Error', 'Failed to load payment accounts. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentAccounts();
  }, [error]);

  const handleFileChange = (e) => {
    setProof(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!proof) {
      error('Proof Required', 'Please upload proof image.');
      return;
    }

    const formData = new FormData();
    formData.append('userEnteredAmount', userEnteredAmount);
    formData.append('proof', proof);

    try {
      await paymentService.addPayment(formData);
      success('Request Submitted', 'Offline recharge request submitted successfully.');
      navigate('/payments');
    } catch (err) {
      console.error('Failed to save recharge', err);
      error('Request Failed', err?.response?.data || err.message || 'Failed to submit recharge request.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-10 min-h-screen bg-gray-100"
    >
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-3xl font-bold mb-2 text-gray-900">Offline Recharge Request</h2>
        <p className="text-sm text-gray-600 mb-6">
          Account transfer karein, phir proof screenshot upload karke request submit karein.
        </p>

        <div className="mb-6 bg-indigo-100 p-4 rounded-lg">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xl font-semibold mb-2">Payment Information</h3>
            <Link to="/payments/online" className="text-sm text-indigo-700 underline">
              Go to Online Payment
            </Link>
          </div>

          <p>Transfer the amount to one of the following accounts and upload the screenshot:</p>
          {loading ? (
            <div className="flex items-center justify-center mt-4">
              <FaSpinner className="animate-spin mr-2" />
              <span>Loading payment accounts...</span>
            </div>
          ) : (
            <ul className="list-disc pl-5 mt-2">
              {paymentAccounts.map((account) => (
                <li key={account.paymentAccountId} className="mb-1">
                  <strong>{account.bankName}:</strong> {account.accountNumber}
                  {account.title && <span className="text-gray-600 text-sm ml-2">({account.title})</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-1" htmlFor="userEnteredAmount">Amount</label>
            <input
              type="number"
              id="userEnteredAmount"
              name="userEnteredAmount"
              placeholder="Enter Amount"
              value={userEnteredAmount}
              onChange={(e) => setUserEnteredAmount(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1" htmlFor="proof">Upload Proof Screenshot</label>
            <input
              type="file"
              id="proof"
              name="proof"
              onChange={handleFileChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              accept="image/*"
              required
            />
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              className="flex-1 bg-indigo-600 text-white p-3 rounded-lg hover:bg-indigo-700 transition-colors duration-200 flex items-center justify-center transform hover:scale-105 shadow-md"
            >
              <FaSave className="mr-2" /> Save
            </button>
            <Link
              to="/payments"
              className="flex-1 bg-gray-600 text-white p-3 rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center transform hover:scale-105 shadow-md"
            >
              <FaTimes className="mr-2" /> Cancel
            </Link>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default PaymentForm;
